import React, { Suspense } from 'react';
import RouteFallback from './RouteFallback';
import type ClaimLandingComponent from './ClaimLanding';

/**
 * Code-split boundary for the teaser report.
 *
 * ClaimLanding pulls in the whole `teaser/` tree — hero and score block,
 * evidence cards, the locked silhouette, the CTA cluster, the copy and row
 * derivation modules — but it renders on exactly one arrival: a visitor who
 * followed a claim link (`claimParams.scanId && authEnabled` in HeroSection,
 * or a claim identity matching the active #/scan/:id route in
 * AnonymousClaimHandoff). Every other visitor to the scanner downloaded all
 * of it and rendered none of it.
 *
 * Epic #941's teaser work (#946-#951) grew that subtree past the entry
 * chunk's remaining headroom, which is what surfaced this. Splitting it is
 * the fix on the merits either way: the budget in
 * scripts/check-bundle-budget.js is a ratchet, and raising it to accommodate
 * a payload most visitors never execute would spend the ratchet on the wrong
 * thing.
 *
 * The teaser is below the fold of a fresh navigation and behind a claim link,
 * so a brief fallback costs nothing a synchronous parse would not have cost
 * in blocking time — RouteFallback is the same treatment the lazy page routes
 * use (StandalonePagesRoute, SecondaryPagesRoute).
 */
const ClaimLanding = React.lazy(() => import('./ClaimLanding'));

type ClaimLandingProps = React.ComponentProps<typeof ClaimLandingComponent>;

export default function LazyClaimLanding(props: ClaimLandingProps) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <ClaimLanding {...props} />
    </Suspense>
  );
}
